import {
  Button,
  Form,
  Input,
  List,
  Upload,
  Skeleton,
  Row,
  Col,
  Tooltip,
  Popover,
} from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PaperClipOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import Editor from "./Editor";
import { useAlert } from "../context/AlertProvider";
import { EmailContext } from "../context/EmailContext";
import CopyText from "../components/web2lead/CopyText";
import copy from "../assets/images/webToLead/copy.png";
import { sendEmail } from "../actionCreator/emailSetupActionCreater";
import SubscriptionModal from "../components/web2lead/SubscriptionModal";
import { getSubscriptionUserInfo, getUserInfo } from "../utils/utils";
import { AuthContext } from "../context/AuthContext";
import generateRes from "../assets/svg/generate-response.svg";
import { useEmailWebSocket } from "./hooks/useEmailWebSocket";
import { getAuthMethod } from "../services/HttpServices";
import { getClientLeadsUrl } from "../constant/Constant";
import {
  createMailMergeCampaign,
  getMailMergeCampaignById,
} from "../services/mailMergeService";
import {
  getEmailTemplates,
  clearMessages,
  generateEmailAI,
  createAISession,
} from "../actionCreator/genAIActionCreator";
import { ReactComponent as SendIcon } from "../assets/svg/icons/send-email.svg";
import aiIcon from "../assets/svg/icons/ai-assist.svg";
import rewrite from "../assets/svg/icons/rewrite.svg";
import { ReactComponent as DeleteIcon } from "../assets/images/webToLead/delete.svg";
import {
  Minus,
  Maximize2,
  Minimize2,
  X as CloseIcon,
  Mail,
  Plus,
  Search,
  Check,
  Activity,
} from "lucide-react";

const EmailForm = ({
  children,
  recipientEmail,
  empId,
  payload,
  callback,
  convertToLead,
  onConvertToLead,
  recipients: initialRecipientsProp,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const editorRef = useRef(null);
  const hasFetchedTemplates = useRef(false);

  const { subscribeToSession, unsubscribeFromSession } =
    useEmailWebSocket("email-channel-1");

  const { showAlert } = useAlert();
  const { isEmailSetupCreate } = useContext(AuthContext);
  const subscription = getSubscriptionUserInfo();
  const currentDate = moment();
  const validToDate = moment(subscription?.validTo);
  const isExpired = validToDate.isBefore(currentDate);

  const { isEmailConfigured, emailConfiguration } = useContext(EmailContext);

  const [fileList, setFileList] = useState([]);
  const [embeddedImgs, setEmbeddedImgs] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [position, setPosition] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);
  const [newRecipientInput, setNewRecipientInput] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [bulkSuccessData, setBulkSuccessData] = useState(null);
  const [isMoreRecipientsOpen, setIsMoreRecipientsOpen] = useState(false);
  const [moreRecipientsSearch, setMoreRecipientsSearch] = useState("");
  const addRecipientInputRef = useRef(null);
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialLeft: 0,
    initialTop: 0,
  });
  const modalRef = useRef(null);

  const handleMouseDown = (e) => {
    if (
      e.target.closest("button") ||
      e.target.closest(".no-drag") ||
      e.target.closest("svg")
    ) {
      return;
    }
    if (isMaximized) return;

    const el = modalRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      initialLeft: rect.left,
      initialTop: rect.top,
    };

    const handleMouseMove = (moveEvent) => {
      if (!dragRef.current.isDragging) return;
      const deltaX = moveEvent.clientX - dragRef.current.startX;
      const deltaY = moveEvent.clientY - dragRef.current.startY;
      const newLeft = dragRef.current.initialLeft + deltaX;
      const newTop = dragRef.current.initialTop + deltaY;

      const modalWidth = el.offsetWidth || 860;
      const modalHeight = el.offsetHeight || 570;
      const boundedLeft = Math.max(
        10,
        Math.min(window.innerWidth - modalWidth - 10, newLeft),
      );
      const boundedTop = Math.max(
        10,
        Math.min(window.innerHeight - modalHeight - 10, newTop),
      );

      setPosition({ x: boundedLeft, y: boundedTop });
    };

    const handleMouseUp = () => {
      dragRef.current.isDragging = false;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStreamRaw, setAiStreamRaw] = useState("");
  const [aiLive, setAiLive] = useState({ subject: "", body: "" });
  const [isStreamingStopped, setIsStreamingStopped] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [showAiPromptBox, setShowAiPromptBox] = useState(false);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [leadSuggestions, setLeadSuggestions] = useState([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const skeletonTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  const streamMessages = useSelector(
    (state) => state.genAIReducer.streamMessages,
  );
  const EMAIL_TEMPLATES = useSelector(
    (state) => state.genAIReducer.emailTemplates,
  );
  const isLoading = useSelector((state) => state.genAIReducer.loading);

  const handleCancel = () => {
    setOpen(false);
    setIsMinimized(false);
    setIsMaximized(false);
    setPosition(null);
    form.resetFields();
    setError();
    setFileList([]);
    setEmbeddedImgs([]);
    setSelectedTemplate(null);
    setAiStreamRaw("");
    setAiLive({ subject: "", body: "" });
    setIsStreamingStopped(false);
    setAiPrompt("");
    setShowAiPromptBox(false);
    setSkeletonLoading(false);
    setRecipients([]);
    setIsAddingRecipient(false);
    setNewRecipientInput("");
    setIsExpanded(false);
    setIsMoreRecipientsOpen(false);
    setMoreRecipientsSearch("");
    setLeadSuggestions([]);
    setShowSuggestions(false);
    setIsSearchingLeads(false);
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    dispatch(clearMessages());
    unsubscribeFromSession();
  };

  const handleFilesChange = ({ file, fileList }) => {
    const isLt10MB = file.size / 1024 / 1024 < 10;
    if (!isLt10MB) {
      showAlert("error", `${file.name} is larger than 10MB!`);
    } else {
      setFileList(fileList);
    }
  };

  const handleRemove = (file) => {
    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
  };

  const onOpen = () => {
    setAiStreamRaw("");
    setAiLive({ subject: "", body: "" });
    setIsStreamingStopped(false);
    setIsMinimized(false);
    setOpen(true);
  };

  const normalizeRecipient = (item, defaultName = "") => {
    if (!item) return null;
    if (typeof item === "string") {
      const trimmed = item.trim();
      if (!trimmed) return null;
      const match = trimmed.match(/^(.*?)\s*<(.+@.+)>$/);
      if (match) {
        return {
          id: match[2].trim(),
          name: match[1].trim() || match[2].trim(),
          email: match[2].trim(),
        };
      }
      return {
        id: trimmed,
        name: defaultName || trimmed,
        email: trimmed,
      };
    }
    if (typeof item === "object") {
      const email = item.email || item.emailId || item.recipientEmail || "";
      const name =
        item.name ||
        [item.firstname || item.firstName, item.lastname || item.lastName]
          .filter(Boolean)
          .join(" ") ||
        item.fullName ||
        email;
      if (!email) return null;
      return {
        id: item.id || email,
        name: name || email,
        email: email,
      };
    }
    return null;
  };

  useEffect(() => {
    if (!isOpen) return;

    let initialList = [];

    if (
      Array.isArray(initialRecipientsProp) &&
      initialRecipientsProp.length > 0
    ) {
      initialList = initialRecipientsProp
        .map((item) => normalizeRecipient(item))
        .filter(Boolean);
    } else if (Array.isArray(recipientEmail) && recipientEmail.length > 0) {
      initialList = recipientEmail
        .map((item) => normalizeRecipient(item))
        .filter(Boolean);
    } else if (typeof recipientEmail === "string" && recipientEmail.trim()) {
      const parts = recipientEmail
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const leadName = payload
        ? [payload.firstname, payload.lastname].filter(Boolean).join(" ") ||
          payload.fullName ||
          payload.name ||
          ""
        : "";
      initialList = parts
        .map((email, idx) =>
          normalizeRecipient(email, idx === 0 ? leadName : ""),
        )
        .filter(Boolean);
    }

    setRecipients(initialList);
    setIsExpanded(false);
    setIsAddingRecipient(false);
    setNewRecipientInput("");

    form.setFieldsValue({
      from: emailConfiguration?.email,
      to: initialList.map((r) => r.email).join(", "),
    });
  }, [
    isOpen,
    recipientEmail,
    initialRecipientsProp,
    emailConfiguration,
    payload,
    form,
  ]);

  useEffect(() => {
    if (isOpen) {
      const toValue = recipients
        .map((r) => r.email)
        .filter(Boolean)
        .join(", ");
      form.setFieldsValue({ to: toValue });
    }
  }, [recipients, form, isOpen]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAddRecipient = () => {
    if (!newRecipientInput || !newRecipientInput.trim()) return;

    // Support multiple emails separated by comma, semicolon, newline, or whitespace
    const rawParts = newRecipientInput
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const updated = [...recipients];
    let hasInvalid = false;
    let addedCount = 0;

    rawParts.forEach((part) => {
      const match = part.match(/^(.*?)\s*<(.+@.+)>$/);
      const email = match ? match[2].trim() : part.trim();
      const name = match ? match[1].trim() : "";

      if (emailRegex.test(email)) {
        const exists = updated.some(
          (r) => r.email?.toLowerCase() === email.toLowerCase(),
        );
        if (!exists) {
          updated.push({
            id: `${email}-${Date.now()}-${Math.random()}`,
            name: name || email,
            email: email,
          });
          addedCount++;
        }
      } else {
        hasInvalid = true;
      }
    });

    if (hasInvalid && addedCount === 0) {
      showAlert("error", "Please enter a valid email address");
      return;
    }

    if (hasInvalid && addedCount > 0) {
      showAlert("warning", "Some invalid email addresses were skipped");
    }

    setRecipients(updated);
    setNewRecipientInput("");
    setIsAddingRecipient(false);
    setError();
  };

  const handleRemoveRecipient = (indexToRemove) => {
    setRecipients((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const getInitials = (str) => {
    if (!str) return "L";
    const parts = str.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return str.slice(0, 2).toUpperCase();
  };

  const renderMoreRecipientsPopover = () => {
    const hiddenRecipients = recipients.slice(4);
    const filteredHidden = moreRecipientsSearch.trim()
      ? hiddenRecipients.filter(
          (r) =>
            r.name?.toLowerCase().includes(moreRecipientsSearch.toLowerCase()) ||
            r.email?.toLowerCase().includes(moreRecipientsSearch.toLowerCase()),
        )
      : hiddenRecipients;

    return (
      <div className="tw-w-72 tw-bg-white tw-rounded-xl tw-p-3 tw-shadow-xl tw-border tw-border-slate-100">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-2 tw-mb-2 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-text-xs tw-font-bold tw-text-slate-800">
              Other Recipients
            </span>
            <span className="tw-text-[11px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full tw-bg-[#EEF0FF] tw-text-[#5D44FF]">
              {hiddenRecipients.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsMoreRecipientsOpen(false);
              setIsExpanded(true);
            }}
            className="tw-text-[11px] tw-font-semibold tw-text-[#5D44FF] hover:tw-underline tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
          >
            Expand all
          </button>
        </div>

        {/* Search input if more than 5 recipients */}
        {hiddenRecipients.length > 5 && (
          <div className="tw-mb-2">
            <div className="tw-flex tw-items-center tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200 focus-within:tw-border-[#5D44FF] tw-rounded-lg tw-h-7 tw-px-2 tw-transition-colors">
              <Search size={12} className="tw-text-slate-400 tw-mr-1.5 tw-shrink-0" />
              <input
                type="text"
                placeholder="Filter recipients..."
                value={moreRecipientsSearch}
                onChange={(e) => setMoreRecipientsSearch(e.target.value)}
                className="tw-w-full tw-bg-transparent tw-border-none tw-outline-none tw-text-[11px] tw-text-slate-700 placeholder:tw-text-slate-400"
              />
              {moreRecipientsSearch && (
                <button
                  type="button"
                  onClick={() => setMoreRecipientsSearch("")}
                  className="tw-text-slate-400 hover:tw-text-slate-600 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
                >
                  <CloseIcon size={11} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* List of Hidden Recipients */}
        <div className="tw-max-h-56 tw-overflow-y-auto custom-scrollbar tw-flex tw-flex-col tw-gap-1 tw-pr-0.5">
          {filteredHidden.length === 0 ? (
            <div className="tw-text-center tw-py-3 tw-text-xs tw-text-slate-400">
              No matching recipients
            </div>
          ) : (
            filteredHidden.map((recipient) => {
              const originalIndex = recipients.findIndex(
                (r) =>
                  (r.id && recipient.id && r.id === recipient.id) ||
                  (r.email && recipient.email && r.email.toLowerCase() === recipient.email.toLowerCase()),
              );

              return (
                <div
                  key={recipient.id || recipient.email}
                  className="tw-flex tw-items-center tw-justify-between tw-p-1.5 tw-rounded-lg hover:tw-bg-slate-50 tw-transition-colors"
                >
                  <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1 tw-mr-2">
                    <div className="tw-size-6 tw-rounded-full tw-bg-[#EEF0FF] tw-text-[#5D44FF] tw-font-bold tw-text-[10px] tw-flex tw-items-center tw-justify-center tw-shrink-0">
                      {getInitials(recipient.name || recipient.email)}
                    </div>
                    <div className="tw-min-w-0 tw-flex-1">
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-800 tw-truncate">
                        {recipient.name || recipient.email}
                      </div>
                      {recipient.name && recipient.email && recipient.name !== recipient.email && (
                        <div className="tw-text-[10px] tw-text-slate-400 tw-truncate">
                          {recipient.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(originalIndex)}
                    className="tw-p-1 tw-text-slate-300 hover:tw-text-red-500 hover:tw-bg-red-50 tw-rounded tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-items-center tw-transition-colors tw-shrink-0"
                    title="Remove recipient"
                  >
                    <CloseIcon size={13} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const searchLeadsForRecipient = async (query) => {
    if (!query || query.trim().length < 1) return [];
    const trimmed = query.trim();
    try {
      const [emailRes, termRes] = await Promise.allSettled([
        getAuthMethod(
          `${getClientLeadsUrl}?emailId.contains=${encodeURIComponent(trimmed)}&size=10`,
        ),
        getAuthMethod(
          `${getClientLeadsUrl}?term=${encodeURIComponent(trimmed)}&size=10`,
        ),
      ]);

      const emailList =
        emailRes.status === "fulfilled" && Array.isArray(emailRes.value?.data)
          ? emailRes.value.data
          : [];
      const termList =
        termRes.status === "fulfilled" && Array.isArray(termRes.value?.data)
          ? termRes.value.data
          : [];

      const map = new Map();
      [...emailList, ...termList].forEach((lead) => {
        const email = lead?.emailId?.trim() || lead?.email?.trim();
        if (email && !map.has(email.toLowerCase())) {
          map.set(email.toLowerCase(), lead);
        }
      });

      return Array.from(map.values());
    } catch (e) {
      console.error("Error searching leads:", e);
      return [];
    }
  };

  const handleRecipientInputChange = (e) => {
    const value = e.target.value;
    setNewRecipientInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value || !value.trim()) {
      setLeadSuggestions([]);
      setShowSuggestions(false);
      setIsSearchingLeads(false);
      return;
    }

    setIsSearchingLeads(true);
    setShowSuggestions(true);

    searchTimeoutRef.current = setTimeout(async () => {
      const results = await searchLeadsForRecipient(value);
      setLeadSuggestions(results);
      setIsSearchingLeads(false);
    }, 300);
  };

  const handleSelectLeadSuggestion = (lead) => {
    const email = lead.emailId || lead.email;
    if (!email) return;

    const name =
      [lead.firstName || lead.firstname, lead.lastName || lead.lastname]
        .filter(Boolean)
        .join(" ") ||
      lead.fullName ||
      lead.name ||
      email;

    const exists = recipients.some(
      (r) => r.email?.toLowerCase() === email.toLowerCase(),
    );

    if (!exists) {
      setRecipients((prev) => [
        ...prev,
        {
          id: lead.id || `${email}-${Date.now()}`,
          name: name,
          email: email,
        },
      ]);
    }

    setNewRecipientInput("");
    setLeadSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        addRecipientInputRef.current &&
        !(
          addRecipientInputRef.current.contains?.(event.target) ||
          addRecipientInputRef.current.input?.contains?.(event.target)
        )
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (payload?.subject) {
      form.setFieldsValue({ subject: payload?.subject });
    }

    const rawBody =
      payload?.bodyTemplate ||
      payload?.body ||
      payload?.content ||
      payload?.html ||
      "";

    if (rawBody) {
      let cleanBody = rawBody;
      if (
        /&lt;\s*(div|p|br|span|html|body|table|tr|td|blockquote|a|h[1-6])/i.test(
          cleanBody
        )
      ) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = cleanBody;
        cleanBody = tempDiv.textContent || tempDiv.innerText || cleanBody;
      }
      form.setFieldsValue({ body: cleanBody });
      if (editorRef.current) {
        editorRef.current.setTemplate(cleanBody);
      }
    } else if (payload?.campaignId) {
      getMailMergeCampaignById(payload.campaignId)
        .then((campaignData) => {
          if (campaignData) {
            const fetchedSubject = campaignData.subject;
            const fetchedBody =
              campaignData.bodyTemplate || campaignData.body || "";
            if (fetchedSubject && !form.getFieldValue("subject")) {
              form.setFieldsValue({ subject: fetchedSubject });
            }
            if (fetchedBody) {
              let cleanBody = fetchedBody;
              if (
                /&lt;\s*(div|p|br|span|html|body|table|tr|td|blockquote|a|h[1-6])/i.test(
                  cleanBody
                )
              ) {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = cleanBody;
                cleanBody =
                  tempDiv.textContent || tempDiv.innerText || cleanBody;
              }
              form.setFieldsValue({ body: cleanBody });
              if (editorRef.current) {
                editorRef.current.setTemplate(cleanBody);
              }
            }
            if (
              Array.isArray(campaignData.recipients) &&
              campaignData.recipients.length > 0
            ) {
              setRecipients((prev) => {
                if (prev && prev.length > 0) return prev;
                return campaignData.recipients
                  .map((r) => normalizeRecipient(r))
                  .filter(Boolean);
              });
            }
          }
        })
        .catch((err) => {
          console.error("Failed to prefill reused campaign content:", err);
        });
    }
  }, [isOpen, payload, form]);

  useEffect(() => {
    if (!EMAIL_TEMPLATES?.length && !isLoading && !hasFetchedTemplates.current) {
      hasFetchedTemplates.current = true;
      dispatch(getEmailTemplates());
    }
  }, [EMAIL_TEMPLATES, isLoading, dispatch]);

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);

      form.setFieldsValue({
        subject: "",
        body: "",
      });

      if (editorRef.current) {
        editorRef.current.setTemplate("");
      }

      return;
    }
    const template = EMAIL_TEMPLATES.find((t, i) => i + 1 === templateId);
    const user = getUserInfo();
    const userName = user?.firstName + " " + user?.lastName || "Your Name";
    const company = user?.companyName || "Company Name";
    const leadName = payload?.firstname + " " + payload?.lastname || "there";

    const replacedBody = template?.body
      .replaceAll("[Name]", leadName)
      .replaceAll("[Your Name]", userName)
      .replaceAll("[Company Name]", company)
      ?.split("\n\n")
      .map((p) => p.replace(/\n/g, "<br />"))
      .map((p) => `<p>${p}</p>`)
      .join("");
    setSelectedTemplate({
      ...template,
      body: replacedBody,
    });

    form.setFieldsValue({
      subject: template?.subject,
      body: replacedBody,
    });

    if (editorRef.current) {
      editorRef.current.setTemplate(replacedBody);
    }
  };

  const extractAIFieldsFromString = (raw) => {
    if (!raw) return { subject: "", body: "" };
    const subjMatch = raw.match(/<subject>([\s\S]*?)<\/subject>/i);
    const subject = subjMatch ? subjMatch[1].trim() : "";
    const body = subjMatch
      ? raw.replace(/[\s\S]*<\/subject>/i, "").trim()
      : raw.trim();
    return { subject, body };
  };

  useEffect(() => {
    if (!streamMessages) return;

    // stop skeleton loader once we have streaming content
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
      setSkeletonLoading(false);
    }

    const combined = Array.isArray(streamMessages)
      ? streamMessages.join("")
      : String(streamMessages);

    if (isStreamingStopped) return;

    setAiStreamRaw(combined);

    const parsed = extractAIFieldsFromString(combined);

    setAiLive((prev) => ({
      subject: parsed.subject || prev.subject,
      body: parsed.body || prev.body,
    }));
  }, [streamMessages, isStreamingStopped]);

  useEffect(() => {
    if (!isLoading && aiStreamRaw) {
      const parsed = extractAIFieldsFromString(aiStreamRaw);

      setAiLive({
        subject: parsed.subject || aiLive.subject,
        body: parsed.body || aiLive.body,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handlePromptBasedAISuggestion = async () => {
    setShowAiPromptBox(true);
    setError();
    if (!aiPrompt.trim()) {
      showAlert("warning", "Please enter a prompt");
      return;
    }

    try {
      setError();
      setAiLoading(true);
      setSkeletonLoading(true);
      dispatch(clearMessages());
      setAiStreamRaw("");
      setAiLive({ subject: "", body: "" });
      setIsStreamingStopped(false);

      // skeleton fallback (max 12 seconds)
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
      }
      skeletonTimeoutRef.current = setTimeout(() => {
        setIsStreamingStopped(true);
        unsubscribeFromSession();
        setAiLoading(false);
        setSkeletonLoading(false);
        showAlert(
          "warning",
          "AI took too long to respond. Try again or refine your prompt.",
        );
      }, 12000);

      const res = await dispatch(createAISession());
      const sessionId = res?.data?.sessionId;

      if (!sessionId) {
        showAlert("error", "Unable to create AI session.");
        setAiLoading(false);
        setSkeletonLoading(false);
        if (skeletonTimeoutRef.current) {
          clearTimeout(skeletonTimeoutRef.current);
          skeletonTimeoutRef.current = null;
        }
        return;
      }

      await subscribeToSession(sessionId);

      const user = getUserInfo();
      const isMailMerge = recipients && recipients.length > 1;

      const leadData = isMailMerge ? {
        firstName: "{{first_name}}",
        lastName: "{{last_name}}",
        title: "",
        companyName: "{{company_name}}",
      } : {
        firstName: payload?.firstname || payload?.firstName,
        lastName: payload?.lastname || payload?.lastName,
        title: payload?.title,
        companyName: payload?.companyName,
      };

      const finalUserInput = isMailMerge
        ? `${aiPrompt}. Note: please preserve the exact template variables {{first_name}}, {{last_name}}, and {{company_name}} in your response without replacing them or inventing names.`
        : aiPrompt;

      const payloadObj = {
        sessionId,
        userInput: finalUserInput,
        senderFullName: user?.firstName + " " + user?.lastName,
        lead: leadData,
      };

      await dispatch(generateEmailAI(payloadObj));
    } catch (err) {
      console.error(err);
      showAlert("error", "AI generation failed");
      setAiLoading(false);
      setSkeletonLoading(false);
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
    } finally {
      setAiLoading(false);
    }
  };

  const addSrcToImages = (htmlString, imagesArray) => {
    return htmlString.replace(
      /<img\s+([^>]*data-ck-upload-id="([^"]+)")/g,
      (match, fullAttribute, uploadId) => {
        const image = imagesArray.find((img) => img.id === uploadId);
        if (image) {
          return `<img ${fullAttribute} src="${image.cidUrl}"`;
        }
        return match;
      },
    );
  };

  const onSubmit = async (values) => {
    const recipientEmails = recipients
      .map((r) => (typeof r === "string" ? r.trim() : r.email?.trim()))
      .filter(Boolean);

    if (!recipientEmails.length) {
      setError("At least one recipient is required");
      showAlert("error", "Please add at least one recipient");
      return;
    }

    setLoading(true);

    const finalSubject =
      aiLive.subject || values.subject || selectedTemplate?.subject;

    const finalBody = aiLive.body || values?.body || selectedTemplate?.body;

    const finalMailBody = addSrcToImages(finalBody, embeddedImgs);

    const data = new FormData();

    data.append(
      "request",
      JSON.stringify({
        from: values.from,
        to: recipientEmails.join(", "),
        recipients: recipientEmails,
        subject: finalSubject,
        body: finalMailBody,
        ...payload,
        context: { employee_id: empId },
      }),
    );

    fileList.forEach((file) => {
      data.append("attachments", file.originFileObj);
    });

    embeddedImgs.forEach((img) => {
      if (finalMailBody.includes(img.id)) {
        data.append("inlineAttachments", img.file);
      }
    });

    if (recipientEmails.length > 1) {
      setLoading(true);
      try {
        const backendRecipients = recipients.map((r) => {
          let rName = "";
          let rEmail = "";
          let rCompany = "";
          let rId = null;

          if (typeof r === "string") {
            rEmail = r.trim();
          } else {
            rEmail = r.email?.trim() || "";
            rName = r.name || r.fullName || "";
            rCompany = r.companyName || "";
            rId = r.id;
          }

          const nameParts = rName.split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";

          let clientLeadId = null;
          if (typeof rId === "string" && !rId.includes("@") && !isNaN(Number(rId))) {
            clientLeadId = Number(rId);
          } else if (typeof rId === "number") {
            clientLeadId = rId;
          }

          return {
            clientLeadId: clientLeadId,
            email: rEmail,
            firstName: firstName,
            lastName: lastName,
            companyName: rCompany,
          };
        }).filter(r => r.email);

        const createdCampaign = await createMailMergeCampaign({
          name: finalSubject || "Bulk Email Campaign",
          subject: finalSubject,
          from: values.from || emailConfiguration?.email || "",
          recipients: backendRecipients,
          bodyTemplate: finalMailBody,
        });

        handleCancel();
        callback && callback();

        setBulkSuccessData({
          recipientCount: backendRecipients.length,
          sentCount: backendRecipients.length,
          campaignId: createdCampaign?.id,
        });
      } catch (e) {
        console.error("Error creating mail merge campaign record:", e);
        showAlert("error", "Failed to create Mail Merge campaign. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    let res;

    if (convertToLead) {
      await onConvertToLead();
      res = await dispatch(sendEmail(data));
    } else {
      res = await dispatch(sendEmail(data));
    }

    if (res?.status === 200) {
      handleCancel();
      callback && callback();
      showAlert(
        "success",
        convertToLead
          ? "The email has been sent and a lead has been created successfully."
          : "Email sent successfully!",
      );
    } else {
      const errMsg =
        res?.response?.status === 500
          ? "Something went wrong. Please try again."
          : res?.response?.status === 403
            ? "We could not send your email because the necessary permission was not granted. Please go back and make sure to check the box to allow email access when reconnecting your account."
            : res?.response?.data?.title;
      showAlert("error", errMsg);
      setError(errMsg);
    }

    setLoading(false);
  };

  useEffect(() => {
    return () => {
      dispatch(clearMessages());
      unsubscribeFromSession();
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fixHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/\n/g, "<br/>")
      .replace(/ {2,}/g, (m) => "&nbsp;".repeat(m.length));
  };

  const stripHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleStopStreaming = () => {
    setIsStreamingStopped(true);
    unsubscribeFromSession();
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    setSkeletonLoading(false);
  };

  const applySuggestionsToForm = () => {
    form.setFieldsValue({
      subject: selectedTemplate ? selectedTemplate?.subject : aiLive.subject,
      body: fixHtml(aiLive.body),
    });

    if (editorRef.current) {
      editorRef.current.setTemplate(fixHtml(aiLive.body));
    }
    setAiStreamRaw("");
    setAiLive({ subject: "", body: "" });
    dispatch(clearMessages());
    unsubscribeFromSession();
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    setSkeletonLoading(false);
  };

  const renderComposerContent = () => (
    <Row className="tw-h-full tw-w-full">
      <Col span={15} className="tw-flex tw-flex-col tw-justify-between tw-h-full tw-overflow-hidden">
        <Form
          key={isOpen}
          form={form}
          layout="vertical"
          className="tw-flex tw-flex-col tw-justify-between tw-h-full tw-overflow-hidden"
          onFinish={onSubmit}
        >
          <div className="tw-p-4 tw-overflow-hidden tw-flex-1 tw-flex tw-flex-col">
            {!!error && (
              <p className="tw-text-red-500 tw-text-center tw-text-xs tw-pb-2 tw-mb-2">
                {error}
              </p>
            )}

            <div>
              <div className="tw-hidden align-items-center mt5 tw-mb-3">
                <label className="heading13px">From:</label>
                <Form.Item
                  name="from"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter valid E-mail!",
                    },
                    { required: true, message: "From is required" },
                  ]}
                  className="mb0"
                  style={{ width: "100%" }}
                >
                  <Input
                    disabled
                    defaultValue={emailConfiguration?.email}
                    variant="borderless"
                    style={{ width: "100%" }}
                    className="text-color-030334"
                  />
                </Form.Item>
                <div style={{ marginRight: 30 }}>
                  <CopyText
                    text={emailConfiguration?.email}
                    content={
                      <img
                        src={copy}
                        alt=""
                        style={{ width: 15, height: 15 }}
                        className="cursor-pointer"
                      />
                    }
                  />
                </div>
              </div>
              {/* Recipients (To:) Section */}
              <div className="tw-mb-1">
                <div className="tw-flex tw-items-center tw-gap-3">
                  <label className="tw-w-16 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shrink-0">
                    To :
                  </label>
                  <div className="tw-flex-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {recipients
                      .slice(0, isExpanded ? recipients.length : 4)
                      .map((recipient, index) => (
                        <div
                          key={recipient.id || index}
                          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-[#EEF0FF] tw-text-[#5D44FF] tw-border tw-border-solid tw-border-[#E0E7FF] tw-transition-all"
                          title={recipient.email}
                        >
                          <span className="tw-truncate tw-max-w-[180px]">
                            {recipient.name || recipient.email}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipient(index)}
                            className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-[#5D44FF]/70 hover:tw-text-[#5D44FF] tw-cursor-pointer tw-flex tw-items-center tw-transition-colors"
                            title="Remove recipient"
                          >
                            <CloseIcon size={12} className="tw-stroke-[2.5]" />
                          </button>
                        </div>
                      ))}

                    {/* +N more pill with Click Popover Menu */}
                    {!isExpanded && recipients.length > 4 && (
                      <Popover
                        content={renderMoreRecipientsPopover()}
                        trigger="click"
                        open={isMoreRecipientsOpen}
                        onOpenChange={setIsMoreRecipientsOpen}
                        placement="bottomLeft"
                        overlayStyle={{ zIndex: 100005 }}
                        overlayInnerStyle={{
                          padding: 0,
                          borderRadius: 12,
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-transition-colors tw-border tw-border-solid ${
                            isMoreRecipientsOpen
                              ? "tw-bg-[#EEF0FF] tw-text-[#5D44FF] tw-border-[#D7DCFF]"
                              : "tw-bg-[#F1F3F5] hover:tw-bg-slate-200 tw-text-slate-600 tw-border-transparent"
                          } tw-cursor-pointer`}
                          title="Click to view more recipients"
                        >
                          +{recipients.length - 4} more
                        </button>
                      </Popover>
                    )}

                    {/* Show less button when expanded */}
                    {isExpanded && recipients.length > 4 && (
                      <button
                        type="button"
                        onClick={() => setIsExpanded(false)}
                        className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-[#F1F3F5] hover:tw-bg-slate-200 tw-text-slate-600 tw-border-0 tw-cursor-pointer tw-transition-colors"
                      >
                        Show less
                      </button>
                    )}
                  </div>
                </div>

                {/* + Add recipient Row & Dotted Line */}
                <div className="tw-flex tw-items-start tw-gap-3 tw-mt-2">
                  <div className="tw-w-16 tw-shrink-0" />
                  <div className="tw-flex-1">
                    {!isAddingRecipient ? (
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingRecipient(true);
                          setTimeout(
                            () => addRecipientInputRef.current?.focus(),
                            50,
                          );
                        }}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-text-[#5D44FF] tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-transition-colors"
                      >
                        <Plus size={13} className="tw-stroke-[2.5]" />
                        <span>Add recipient</span>
                      </button>
                    ) : (
                      <div className="tw-relative tw-max-w-[380px] tw-mb-2">
                        {/* Unified Search Input Container */}
                        <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-solid tw-border-slate-200 focus-within:tw-border-[#5D44FF] tw-rounded-lg tw-h-8 tw-px-2.5 tw-shadow-sm tw-transition-colors">
                          <Search
                            size={14}
                            className="tw-text-slate-400 tw-shrink-0 tw-mr-2"
                          />
                          <input
                            ref={addRecipientInputRef}
                            type="text"
                            placeholder="Enter recipient email or search lead..."
                            value={newRecipientInput}
                            onChange={handleRecipientInputChange}
                            onFocus={() => {
                              if (leadSuggestions.length > 0) {
                                setShowSuggestions(true);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === ",") {
                                e.preventDefault();
                                handleAddRecipient();
                              } else if (e.key === "Escape") {
                                setIsAddingRecipient(false);
                                setNewRecipientInput("");
                                setShowSuggestions(false);
                              }
                            }}
                            style={{
                              outline: "none",
                              border: "none",
                              boxShadow: "none",
                              background: "transparent",
                            }}
                            className="tw-flex-1 tw-min-w-0 !tw-bg-transparent !tw-border-none !tw-outline-none !tw-shadow-none !tw-ring-0 focus:!tw-outline-none focus:!tw-border-none focus:!tw-ring-0 focus:!tw-shadow-none focus-visible:!tw-outline-none tw-text-xs tw-text-slate-800 placeholder:tw-text-slate-400 tw-h-full tw-px-1"
                          />

                          <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0 tw-ml-1.5">
                            {newRecipientInput?.trim() && (
                              <button
                                type="button"
                                onClick={handleAddRecipient}
                                className="tw-bg-[#5D44FF] hover:tw-bg-[#4c36e0] tw-text-white tw-text-[11px] tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md tw-transition-all tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-shadow-sm active:tw-scale-95"
                              >
                                <span>Add</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setIsAddingRecipient(false);
                                setNewRecipientInput("");
                                setShowSuggestions(false);
                              }}
                              className="tw-p-1 tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-100 tw-rounded-md tw-bg-transparent tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-transition-colors"
                              title="Cancel"
                            >
                              <CloseIcon size={13} />
                            </button>
                          </div>
                        </div>

                        {/* Suggestions Dropdown */}
                        {showSuggestions &&
                          (isSearchingLeads || leadSuggestions.length > 0) && (
                            <div
                              ref={suggestionsRef}
                              className="tw-absolute tw-left-0 tw-top-full tw-mt-1.5 tw-w-full tw-bg-white tw-rounded-xl tw-shadow-[0_12px_32px_rgba(0,0,0,0.18)] tw-border tw-border-solid tw-border-slate-200 tw-z-50 tw-overflow-hidden"
                            >
                              <div className="tw-px-3 tw-py-1.5 tw-bg-slate-50 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-justify-between">
                                <span className="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">
                                  Matching Leads
                                </span>
                                {isSearchingLeads && (
                                  <span className="tw-text-[10px] tw-text-[#5D44FF] tw-flex tw-items-center tw-gap-1">
                                    <span className="tw-animate-spin tw-inline-block tw-size-2.5 tw-border-2 tw-border-[#5D44FF] tw-border-t-transparent tw-rounded-full" />
                                    Searching...
                                  </span>
                                )}
                              </div>

                              <div className="tw-max-h-56 tw-overflow-y-auto custom-scrollbar tw-p-1 tw-flex tw-flex-col tw-gap-0.5">
                                {leadSuggestions.map((lead) => {
                                  const leadName =
                                    [
                                      lead.firstName || lead.firstname,
                                      lead.lastName || lead.lastname,
                                    ]
                                      .filter(Boolean)
                                      .join(" ") ||
                                    lead.fullName ||
                                    lead.name ||
                                    "";
                                  const email = lead.emailId || lead.email;
                                  const company =
                                    lead.companyName || lead.company;
                                  const alreadyAdded = recipients.some(
                                    (r) =>
                                      r.email?.toLowerCase() ===
                                      email?.toLowerCase(),
                                  );

                                  return (
                                    <div
                                      key={lead.id || email}
                                      onClick={() => {
                                        if (alreadyAdded) return;
                                        handleSelectLeadSuggestion(lead);
                                      }}
                                      className={`tw-flex tw-items-center tw-gap-2.5 tw-p-2 tw-rounded-lg tw-transition-all ${
                                        alreadyAdded
                                          ? "tw-opacity-50 tw-cursor-not-allowed tw-bg-slate-50/70"
                                          : "tw-cursor-pointer hover:tw-bg-indigo-50/60 hover:tw-border-indigo-100"
                                      }`}
                                    >
                                      <div className="tw-size-7 tw-rounded-full tw-bg-indigo-100/80 tw-text-[#5D44FF] tw-font-bold tw-text-[11px] tw-flex tw-items-center tw-justify-center tw-shrink-0">
                                        {getInitials(leadName || email)}
                                      </div>
                                      <div className="tw-flex-1 tw-min-w-0">
                                        <div className="tw-flex tw-items-center tw-gap-1.5">
                                          <span className="tw-text-xs tw-font-semibold tw-text-slate-800 tw-truncate">
                                            {leadName || email}
                                          </span>
                                          {company && (
                                            <span className="tw-text-[10px] tw-text-slate-400 tw-truncate">
                                              • {company}
                                            </span>
                                          )}
                                        </div>
                                        <div className="tw-text-[11px] tw-text-slate-500 tw-truncate">
                                          {email}
                                        </div>
                                      </div>
                                      {alreadyAdded ? (
                                        <span className="tw-text-[10px] tw-font-medium tw-text-slate-400 tw-shrink-0">
                                          Added
                                        </span>
                                      ) : (
                                        <span className="tw-text-[11px] tw-font-semibold tw-text-[#5D44FF] tw-shrink-0 hover:tw-underline">
                                          + Select
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    {/* 1px normal line below add recipients only when not in active input mode */}
                    {!isAddingRecipient && (
                      <div className="tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-200 tw-w-64 tw-mt-2.5 tw-mb-3" />
                    )}
                  </div>
                </div>

                {/* Hidden form item for validation */}
                <Form.Item
                  name="to"
                  hidden
                  rules={[
                    {
                      validator: () => {
                        if (!recipients || recipients.length === 0) {
                          return Promise.reject(
                            new Error("Recipient is required"),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </div>

              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3.5">
                <label className="tw-w-16 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shrink-0">
                  Subject :
                </label>

                <Form.Item
                  name="subject"
                  rules={[
                    { required: true, message: "Subject is required" },
                  ]}
                  className="!tw-mb-0 tw-flex-1"
                >
                  {payload?.subject ? (
                    <div className="tw-text-xs tw-font-bold tw-text-slate-900 tw-py-1.5">
                      {payload.subject}
                    </div>
                  ) : (
                    <Input
                      placeholder="Enter email subject..."
                      className="!tw-rounded-lg !tw-border-slate-200 hover:!tw-border-slate-300 focus:!tw-border-[#5D44FF] !tw-text-slate-800 tw-text-xs tw-font-medium !tw-h-9 !tw-px-3 tw-shadow-none tw-transition-colors"
                    />
                  )}
                </Form.Item>
              </div>

              {showAiPromptBox && (
                <div
                  style={{ height: "350px", overflowY: "auto" }}
                  className="mt20"
                >
                  {skeletonLoading && !aiLive.body && (
                    <div className="d-flex flex-column gap-10">
                      <Skeleton.Input
                        active
                        block
                        style={{ height: 20, width: "100%" }}
                      />
                      <Skeleton.Input
                        active
                        block
                        style={{ height: 20, width: "100%" }}
                      />
                      <Skeleton.Input
                        active
                        block
                        style={{ height: 20, width: "100%" }}
                      />
                      <Skeleton.Input
                        active
                        block
                        style={{ height: 20, width: "100%" }}
                      />
                      <Skeleton.Input
                        active
                        block
                        style={{ height: 20, width: "100%" }}
                      />
                    </div>
                  )}

                  {isLoading && aiStreamRaw && !aiLive.body && (
                    <div className="p10 mb20 card px-10 py-10">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="heading16px fw-semibold">
                          AI (Streaming...)
                        </div>
                        <div>
                          <Button
                            size="small"
                            onClick={handleStopStreaming}
                          >
                            Stop
                          </Button>
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        {aiLive.subject ? (
                          <div className="fw-semibold mb6">
                            {aiLive.subject}
                          </div>
                        ) : (
                          <div className="text-muted mb6">
                            Generating subject…
                          </div>
                        )}

                        <div
                          style={{
                            maxHeight: 220,
                            overflowY: "auto",
                            borderRadius: 4,
                            padding: 8,
                            background: "#fafafa",
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            lineHeight: "1.5",
                          }}
                        >
                          {(aiLive.body || aiStreamRaw)
                            .split("\n")
                            .map((line, index) => (
                              <div key={index}>{line}</div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {!isLoading && (aiLive.body || aiLive.subject) && (
                    <>
                      <div
                        style={{
                          maxHeight: "330px",
                          overflowY: "auto",
                          background:
                            "linear-gradient(180deg, #F2F0FF 0%, #FFFFFF 50%, #F2F0FF 100%)",
                        }}
                        className="p10 mb20 px-10 py-10 tw-border tw-border-theme tw-border-solid tw-rounded-[10px]"
                      >
                        <div className="heading16px fw-semibold mt10">
                          {aiLive.subject || "Suggested subject"}
                        </div>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: fixHtml(aiLive.body || ""),
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className={!!showAiPromptBox ? "tw-hidden" : ""}>
              <Form.Item
                name="body"
                rules={[{ required: true, message: "Content is required" }]}
                className="!tw-mb-0 email-content-error"
              >
                <Editor
                  ref={editorRef}
                  initialData={
                    form.getFieldValue("body") ||
                    payload?.bodyTemplate ||
                    payload?.body ||
                    payload?.content ||
                    ""
                  }
                  setEmbeddedImgs={setEmbeddedImgs}
                  onChange={(data) => form.setFieldsValue({ body: data })}
                  topBar={
                    recipients.length > 1 ? (
                      <div className="tw-px-3.5 tw-py-2 tw-bg-[#F8F9FD] tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-200/90 tw-select-none">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1.5">
                          <div className="tw-inline-flex tw-items-center tw-gap-1.5">
                            <span className="tw-font-mono tw-text-[11px] tw-font-bold tw-text-[#5D44FF] tw-bg-[#EEF0FF] tw-px-1.5 tw-py-0.5 tw-rounded">
                              {"{ }"}
                            </span>
                            <span className="tw-text-xs tw-font-semibold tw-text-slate-700">
                              Insert Variable:
                            </span>
                          </div>

                          <div className="tw-inline-flex tw-items-center tw-text-[11px] tw-text-slate-400 tw-whitespace-nowrap tw-shrink-0">
                            <span>Personalize for each recipient</span>
                          </div>
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                          {[
                            { label: "First Name", tag: "{{first_name}}" },
                            { label: "Last Name", tag: "{{last_name}}" },
                            { label: "Company Name", tag: "{{company_name}}" },
                          ].map((v) => (
                            <button
                              key={v.tag}
                              type="button"
                              onClick={() =>
                                editorRef.current?.insertText(v.tag)
                              }
                              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-medium tw-text-slate-700 hover:tw-text-[#5D44FF] tw-bg-white hover:tw-bg-[#F4F5FF] active:tw-bg-[#E8EBFF] tw-border tw-border-solid tw-border-slate-200/90 hover:tw-border-[#5D44FF]/40 tw-rounded-lg tw-shadow-2xs tw-transition-all tw-cursor-pointer active:tw-scale-95"
                              title={`Insert ${v.tag}`}
                            >
                              <span>{v.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null
                  }
                />
              </Form.Item>
            </div>
            {convertToLead && (
              <div className="tw-p-3 tw-text-xs tw-text-slate-500">
                <b>Note:</b> Sending this email will automatically create a
                lead in the system.
              </div>
            )}

            {!!fileList?.length && (
              <div className="px-2">
                <List
                  size="small"
                  className="attachment-list mt10"
                  dataSource={fileList}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <DeleteIcon
                          key="delete"
                          onClick={() => handleRemove(item)}
                          style={{ color: "red", cursor: "pointer" }}
                        />,
                      ]}
                      style={{ padding: "8px 0" }}
                    >
                      {item.name}
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
          {showAiPromptBox ? (
            <div
              style={{
                background:
                  "linear-gradient(180deg, #F2F0FF 0%, #FFFFFF 50%, #F2F0FF 100%)",
              }}
              className="p10 mb20 tw-mx-4 px-10 py-10 tw-border tw-flex tw-items-center tw-justify-between tw-border-theme tw-border-solid tw-rounded-[10px]"
            >
              <Button
                type="text"
                className="tw-text-xs tw-font-bold tw-text-sub-text"
                disabled={skeletonLoading && !aiLive.body}
                onClick={() => {
                  applySuggestionsToForm();
                  setShowAiPromptBox(false);
                  setAiPrompt();
                }}
              >
                <img src={rewrite} alt="" /> Rewrite
              </Button>
              <div className="tw-flex tw-gap-2">
                <Button
                  type="text"
                  className="tw-text-xs tw-font-bold tw-text-sub-text"
                  onClick={() => {
                    setAiStreamRaw("");
                    setAiLive({ subject: "", body: "" });
                    dispatch(clearMessages());
                    setShowAiPromptBox(false);
                    setAiPrompt();
                  }}
                >
                  Discard
                </Button>
                <Button
                  type="primary"
                  className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
                  disabled={skeletonLoading && !aiLive.body}
                  onClick={() => {
                    applySuggestionsToForm();
                    setShowAiPromptBox(false);
                    setAiPrompt();
                  }}
                >
                  Insert
                </Button>
              </div>
            </div>
          ) : (
            <div className="tw-bg-slate-50 tw-py-3 tw-px-5 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-200 tw-shrink-0">
              <div className="tw-flex tw-gap-2 tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Upload
                    multiple
                    fileList={fileList}
                    beforeUpload={() => false}
                    onChange={handleFilesChange}
                    showUploadList={false}
                  >
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer tw-text-slate-500 hover:tw-text-slate-700 tw-transition-colors">
                      <PaperClipOutlined className="tw-text-sm" />
                      <span className="tw-text-xs tw-font-medium">
                        Max. 10 MB
                      </span>
                    </div>
                  </Upload>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Button
                    loading={loading}
                    type="default"
                    className="!tw-rounded-lg !tw-border-slate-200 !tw-text-slate-700 hover:!tw-bg-slate-100 tw-font-medium"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    loading={loading}
                    type="primary"
                    htmlType="submit"
                    className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4c36e0] tw-font-semibold !tw-rounded-lg tw-shadow-sm tw-flex tw-items-center tw-gap-2"
                  >
                    <SendIcon />
                    Send Email
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Form>
      </Col>
      <Col
        span={9}
        className="tw-bg-slate-50/70 tw-border-l tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-slate-200 tw-flex tw-flex-col tw-justify-between tw-h-full tw-overflow-hidden"
      >
        <div className="tw-p-4 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-200 tw-shrink-0">
          <div className="tw-text-sm tw-mb-3 tw-font-bold tw-flex tw-items-center tw-gap-2 tw-text-slate-800">
            <img src={aiIcon} alt="" style={{ width: 18, height: 18 }} />
            AI Assist
          </div>
          <Input.TextArea
            placeholder="Ask AI to write your email…"
            value={aiPrompt}
            onChange={(e) => {
              setAiPrompt(e.target.value);
              setAiStreamRaw("");
            }}
            rows={3}
            className="!tw-rounded-lg !tw-border-slate-200 !tw-bg-white focus:!tw-border-[#5D44FF] tw-text-xs"
            onPressEnter={handlePromptBasedAISuggestion}
          />
          <Button
            type="primary"
            className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4c36e0] tw-w-full tw-mt-3 tw-font-semibold !tw-rounded-lg tw-shadow-sm tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-xs !tw-text-white [&>span]:!tw-text-white"
            loading={aiLoading}
            disabled={
              !aiPrompt?.length || (skeletonLoading && !aiLive.body)
            }
            onClick={handlePromptBasedAISuggestion}
          >
            <img
              src={generateRes}
              alt=""
              style={{ width: 15, height: 15 }}
            />
            <span className="!tw-text-white">Generate Email</span>
          </Button>
        </div>
        <div className="tw-p-4 tw-flex-1 tw-overflow-y-auto custom-scrollbar">
          <div className="tw-font-bold tw-text-xs tw-uppercase tw-tracking-wider tw-text-slate-500 tw-mb-3">
            Email Templates
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2.5">
            {EMAIL_TEMPLATES.map((t, i) => (
              <div
                key={i}
                className={`tw-bg-white hover:tw-border-[#5D44FF] hover:tw-shadow-sm ${
                  t?.subject === selectedTemplate?.subject
                    ? "tw-bg-indigo-50/60 tw-border-[#5D44FF]"
                    : "tw-border-slate-200"
                } tw-cursor-pointer tw-p-3 tw-border tw-border-solid tw-rounded-xl tw-transition-all tw-duration-150 tw-group`}
                onClick={() => handleTemplateSelect(i + 1)}
              >
                <div className="tw-font-semibold tw-text-xs tw-text-slate-800 group-hover:tw-text-[#5D44FF] tw-transition-colors">
                  {t?.subject}
                </div>
                <div className="tw-line-clamp-2 tw-text-xs tw-text-slate-500 tw-mt-1.5 tw-leading-relaxed">
                  {stripHtml(t?.body)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Col>
    </Row>
  );

  return (
    <>
      <SubscriptionModal
        isOpen={subModal}
        handleCancel={() => setSubModal(false)}
      />

      <div
        className="tw-flex tw-items-center"
        onClick={() => {
          if (!subscription?.id || isExpired) return setSubModal(true);
          if (!isEmailSetupCreate)
            return showAlert(
              "warning",
              "Permission Denied",
              "You do not have permission.",
            );
          if (!isEmailConfigured) return navigate("/settings/email-setup");
          onOpen();
        }}
      >
        {children}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={modalRef}
            style={{
              zIndex: 99999,
              ...(isMinimized
                ? {}
                : isMaximized
                  ? {}
                  : position
                    ? { left: position.x, top: position.y }
                    : {}),
            }}
            className={
              isMinimized
                ? "tw-fixed tw-bottom-0 tw-right-6 tw-w-[360px] tw-max-w-[calc(100vw-24px)] tw-h-11 tw-bg-secondary-color tw-text-text-primary tw-rounded-t-xl tw-shadow-2xl tw-border-t tw-border-x tw-border-b-0 tw-border-solid tw-border-border-secondary tw-flex tw-flex-col tw-overflow-hidden tw-transition-all tw-duration-150 select-none tw-cursor-pointer"
                : isMaximized
                  ? "tw-fixed tw-inset-4 md:tw-inset-8 tw-m-auto tw-max-w-[1240px] tw-max-h-[90vh] tw-w-full tw-h-full tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-border tw-border-solid tw-border-slate-300 tw-flex tw-flex-col tw-overflow-hidden tw-transition-all tw-duration-200"
                  : position
                    ? "tw-fixed tw-w-[860px] tw-max-w-[calc(100vw-32px)] tw-h-[570px] tw-max-h-[calc(100vh-24px)] tw-bg-white tw-rounded-2xl tw-shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] tw-border tw-border-solid tw-border-slate-300 tw-flex tw-flex-col tw-overflow-hidden"
                    : "tw-fixed tw-bottom-3 tw-right-6 tw-w-[860px] tw-max-w-[calc(100vw-32px)] tw-h-[570px] tw-max-h-[calc(100vh-24px)] tw-bg-white tw-rounded-2xl tw-shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] tw-border tw-border-solid tw-border-slate-300 tw-flex tw-flex-col tw-overflow-hidden tw-transition-all"
            }
            onClick={isMinimized ? () => setIsMinimized(false) : undefined}
          >
            {/* Header Bar */}
            <div
              className={`tw-bg-secondary-color tw-text-text-primary tw-h-11 tw-px-4 tw-flex tw-items-center tw-justify-between select-none tw-shrink-0 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary ${
                isMinimized
                  ? "hover:tw-bg-slate-200 tw-transition-colors"
                  : !isMaximized
                    ? "tw-cursor-move"
                    : ""
              }`}
              onMouseDown={!isMinimized ? handleMouseDown : undefined}
              onDoubleClick={
                !isMinimized
                  ? () => setIsMaximized((prev) => !prev)
                  : undefined
              }
            >
              <div className="tw-flex tw-items-center tw-gap-2.5 tw-truncate tw-pointer-events-none">
                <Mail size={16} className="tw-text-[#5D44FF] tw-shrink-0" />
                <span className="tw-text-xs tw-font-bold tw-text-text-primary tw-truncate">
                  Compose Email {emailConfiguration?.email ? `(${emailConfiguration.email})` : recipientEmail ? `- ${recipientEmail}` : ""}
                </span>
              </div>
              <div
                className="tw-flex tw-items-center tw-gap-2 no-drag tw-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                {isMinimized ? (
                  <>
                    <button
                      type="button"
                      className="tw-p-1 tw-rounded hover:tw-bg-slate-200 tw-text-slate-500 hover:tw-text-slate-800 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-items-center"
                      onClick={() => setIsMinimized(false)}
                      title="Expand"
                    >
                      <Maximize2 size={13} />
                    </button>
                    <button
                      type="button"
                      className="tw-p-1 tw-rounded hover:tw-bg-slate-200 tw-text-slate-500 hover:tw-text-slate-800 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-items-center"
                      onClick={handleCancel}
                      title="Close"
                    >
                      <CloseIcon size={15} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="tw-p-1 tw-rounded hover:tw-bg-slate-200 tw-text-slate-500 hover:tw-text-slate-800 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-items-center"
                      onClick={() => setIsMinimized(true)}
                      title="Minimize"
                    >
                      <Minus size={15} />
                    </button>
                    <button
                      type="button"
                      className="tw-p-1 tw-rounded hover:tw-bg-slate-200 tw-text-slate-500 hover:tw-text-slate-800 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-items-center"
                      onClick={() => setIsMaximized((prev) => !prev)}
                      title={isMaximized ? "Restore Size" : "Maximize"}
                    >
                      {isMaximized ? (
                        <Minimize2 size={14} />
                      ) : (
                        <Maximize2 size={14} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="tw-p-1 tw-rounded hover:tw-bg-slate-200 tw-text-slate-500 hover:tw-text-slate-800 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-items-center"
                      onClick={handleCancel}
                      title="Close"
                    >
                      <CloseIcon size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Form Content */}
            <div
              className={
                isMinimized
                  ? "tw-hidden"
                  : "tw-flex-1 tw-min-h-0 tw-overflow-hidden"
              }
            >
              {renderComposerContent()}
            </div>
          </div>,
          document.body,
        )}

      {bulkSuccessData &&
        createPortal(
          <div
            className="tw-fixed tw-inset-0 tw-z-[99999] tw-flex tw-items-center tw-justify-center tw-bg-black/30 tw-backdrop-blur-[2px] tw-p-4 tw-animate-in tw-fade-in tw-duration-200"
            onClick={() => setBulkSuccessData(null)}
          >
            <div
              className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-[420px] tw-p-8 tw-flex tw-flex-col tw-items-center tw-text-center tw-relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Circular Success Badge */}
              <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-[#EEF0FF] tw-flex tw-items-center tw-justify-center tw-mb-5">
                <Check className="tw-w-8 tw-h-8 tw-text-[#5D44FF]" strokeWidth={2.5} />
              </div>

              {/* Title & Description */}
              <h3 className="tw-text-xl tw-font-bold tw-text-slate-900 tw-mb-2">
                Bulk Email Sent!
              </h3>
              <p className="tw-text-sm tw-text-slate-500 tw-mb-6">
                {bulkSuccessData.sentCount} emails have been sent individually.
              </p>

              {/* Metrics Card */}
              <div className="tw-w-full tw-bg-[#F8F9FD] tw-border tw-border-[#ECEEF5] tw-rounded-xl tw-py-4 tw-px-6 tw-mb-7 tw-flex tw-items-center tw-justify-around">
                <div className="tw-flex tw-flex-col tw-items-center">
                  <span className="tw-text-2xl tw-font-bold tw-text-[#5D44FF]">
                    {bulkSuccessData.recipientCount}
                  </span>
                  <span className="tw-text-xs tw-text-slate-500 tw-mt-0.5">Recipients</span>
                </div>
                <div className="tw-flex tw-flex-col tw-items-center">
                  <span className="tw-text-2xl tw-font-bold tw-text-[#5D44FF]">
                    {bulkSuccessData.sentCount}
                  </span>
                  <span className="tw-text-xs tw-text-slate-500 tw-mt-0.5">Emails Sent</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-w-full">
                <button
                  type="button"
                  onClick={() => {
                    setBulkSuccessData(null);
                    navigate("/search-lead");
                  }}
                  className="tw-flex-1 tw-py-2.5 tw-px-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-text-slate-700 tw-font-medium tw-text-sm tw-transition-colors tw-cursor-pointer"
                >
                  Go to Leads
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const campaignId = bulkSuccessData?.campaignId;
                    setBulkSuccessData(null);
                    navigate(
                      campaignId ? `/mail-merge/${campaignId}` : "/mail-merge",
                    );
                  }}
                  className="tw-flex-1 tw-py-2.5 tw-px-4 tw-rounded-lg tw-bg-[#5D44FF] hover:tw-bg-[#4e36f0] tw-text-white tw-font-medium tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-colors tw-border-0 tw-cursor-pointer tw-shadow-sm"
                >
                  <Activity className="tw-w-4 tw-h-4" />
                  <span>View Performance</span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default EmailForm;
